import React, { useState } from 'react';
import {
  ArrowLeft,
  ExternalLink,
  Loader,
  TrendingUp,
  FileText,
  Briefcase,
  Target,
  BarChart3,
  Users,
  Search
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTVNavigation } from '../hooks/useTVNavigation';

const Management = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedWebsite, setSelectedWebsite] = useState(null);

  useTVNavigation({ enabled: true, onBack: () => navigate('/') });

  const managementCategories = [
    {
      id: 1,
      name: "ISF Exchange",
      description: "Investment & Stock Exchange Platform",
      icon: TrendingUp,
      color: "from-blue-500 to-indigo-700",
      website: "https://isfexchange.com",
      image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=800"
    },
    {
      id: 2,
      name: "Pitchdeck",
      description: "Business Presentations & Proposals",
      icon: FileText,
      color: "from-purple-800 to-gray-500",
      website: "https://pitchdeck.co.in",
      image: "https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=800"
    },
    {
      id: 3,
      name: "Business Strategy",
      description: "Strategic planning & management",
      icon: Target,
      color: "from-green-500 to-teal-700",
      website: "https://businessstrategy.com",
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=800"
    },
    {
      id: 4,
      name: "Corporate Solutions",
      description: "Enterprise management tools",
      icon: Briefcase,
      color: "from-orange-500 to-red-700",
      website: "https://corporatesolutions.com",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800"
    },
    {
      id: 5,
      name: "Financial Analytics",
      description: "Data-driven financial insights",
      icon: BarChart3,
      color: "from-gray-400 to-cyan-700",
      website: "https://financialanalytics.com",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800"
    },
    {
      id: 6,
      name: "Project Management",
      description: "Team collaboration & workflows",
      icon: Users,
      color: "from-yellow-500 to-orange-700",
      website: "https://projectmanagement.com",
      image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=800"
    },
    {
      id: 7,
      name: "Market Research",
      description: "Industry trends & analysis",
      icon: Search,
      color: "from-blue-500 to-gray-500",
      website: "https://marketresearch.com",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800"
    }
  ];

  const handleCardClick = (category) => {
    // Cards 3, 4, 5, 6, 7 go to Coming Soon page
    if (category.id === 3 || category.id === 4 || category.id === 5 || category.id === 6 || category.id === 7) {
      navigate("/management/coming-soon");
      return;
    }
    
    setSelectedWebsite(category);
    setIsLoading(true);
    navigate("/management/web");
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleBack = () => {
    setSelectedWebsite(null);
    setIsLoading(false);

    // ⭐ Back to main management page
    navigate("/management");
  };

  const openInNewTab = () => {
    if (selectedWebsite) {
      window.open(selectedWebsite.website, '_blank');
    }
  };

  // ⭐ Check if we are on cards or iframe route
  const isIframePage = window.location.pathname === "/management/web";

  // ⭐ CARDS VIEW → Navbar visible
  if (!isIframePage) {
    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black text-white">
          <div className="px-8 pb-16 max-w-7xl mx-auto mt-48">
            <div data-tv-grid className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {managementCategories.map((category) => {
                const IconComponent = category.icon;

                return (
                  <div
                    key={category.id}
                    data-tv-card
                    tabIndex={0}
                    onClick={() => handleCardClick(category)}
                    onKeyDown={e => e.key === 'Enter' && handleCardClick(category)}
                    className="group relative bg-gray-800 rounded-xl overflow-hidden cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-2xl border border-gray-700 hover:border-gray-500 focus:border-teal-500 focus:outline-none focus:scale-105"
                  >
                    <div className="absolute inset-0">
                      <img
                        src={category.image}
                        alt={category.name}
                        className="w-full h-full object-cover opacity-30 group-hover:opacity-50 transition-opacity duration-300"
                      />
                      <div className={`absolute inset-0 bg-gradient-to-br ${category.color} opacity-60`}></div>
                    </div>

                    <div className="relative p-6 h-64 flex flex-col justify-between">
                      <div>
                        <IconComponent className="w-12 h-12 mb-4 text-white" strokeWidth={1.5} />
                        <h3 className="text-xl font-bold mb-2 text-white">{category.name}</h3>
                        <p className="text-gray-200 text-sm">{category.description}</p>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-white/80">Click to explore</span>
                        <ArrowLeft className="w-5 h-5 text-white rotate-180 transform group-hover:translate-x-2 transition-transform" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="text-center py-8 border-t border-gray-800">
            <p className="text-gray-400 text-sm">
              Gain access to high-quality management tools and resources from trusted industry experts.
            </p>
          </div>
        </div>
      );
  }

  // ⭐ IFRAME VIEW → NO NAVBAR
  return (
    <div className="min-h-screen bg-gray-900">

      <button
        onClick={handleBack}
        className="fixed top-4 left-4 z-50 flex items-center space-x-2 bg-black/70 backdrop-blur-sm text-white px-4 py-2 rounded-full hover:bg-black/90 transition shadow-lg"
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="font-semibold">Back</span>
      </button>

      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-40">
          <div className="text-center">
            <Loader className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-4" />
            <p className="text-white text-lg">Loading {selectedWebsite?.name}...</p>
          </div>
        </div>
      )}

      <div className="relative w-full h-screen">
        <iframe
          src={selectedWebsite?.website}
          onLoad={handleIframeLoad}
          title={selectedWebsite?.name}
          className="w-full h-full border-0"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals"
          loading="lazy"
        />
      </div>
    </div>
  );
};

export default Management;